import { ChangeDetectionStrategy, Component, EnvironmentInjector, NgZone, inject, linkedSignal, signal, viewChild, effect } from "@angular/core";
import {
  IonicModule,
  LoadingController,
  ToastController,
  isPlatform,
  AlertController,
  Platform,
  IonRouterOutlet,
} from "@ionic/angular";
import { CommonModule } from "@angular/common";
import { AuthService } from "./supabase/services/auth.service";
import { initFlowbite } from "flowbite";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { AuthComponent } from "./auth/containers/auth/auth.component";
import { StudentsPage } from "./students/containers/student-list-page/students.page";
import { DataService } from "./supabase/services/data.service";
import { OnboardingComponent } from "./onboarding/onboarding.component";
import { Storage } from "@ionic/storage-angular";
import { SwUpdate } from "@angular/service-worker";
import { Network } from "@capacitor/network";
import { Capacitor } from "@capacitor/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { CalendarSyncService } from "./shared/services/calendar-sync.service";
import { SplashScreen } from "@capacitor/splash-screen";
import { TrackingBannerComponent } from "./shared/components/tracking-banner/tracking-banner.component";
import { AppInitializerService } from "./core/services/app-initializer.service";
@Component({
    selector: "app-root",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"],
    imports: [
        IonicModule,
        CommonModule,
        AuthComponent,
        OnboardingComponent,
        TrackingBannerComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  public environmentInjector = inject(EnvironmentInjector);
  session = this.authService.currentSessionSignal;
  component = StudentsPage;
  user = null;
  onboardingComplete = signal<boolean>(true);
  isSignedIn = linkedSignal(() => this.authService.currentSessionSignal() !== null);
  noNetworkToast: any;
  notConnected: boolean = false;
  public ionStorage = null;
  public isAndroid = isPlatform("android");
  private authCheckDone = false;

  private outlet = viewChild<IonRouterOutlet>("outlet");
  private onboarding = viewChild<OnboardingComponent>("onboarding");
  private auth = viewChild<AuthComponent>("auth");

  constructor(
    private loadingCtrl: LoadingController,
    private authService: AuthService,
    private dataService: DataService,
    private toastController: ToastController,
    private storage: Storage,
    private swUpdate: SwUpdate,
    private zone: NgZone,
    private router: Router,
    private alertController: AlertController,
    private platform: Platform,
    private calendarSyncService: CalendarSyncService,
    private appInitializerService: AppInitializerService
  ) {
    this.checkLocalStorageAuth();
    this.processCurrentUrl();
    initFlowbite();
    const delay = isPlatform("capacitor") ? 1000 : 0;

    effect(() => {
      if (this.outlet() || this.onboarding() || this.auth()) {
        setTimeout(() => {
          document.body.classList.add("app-loaded");
        }, delay);
      }
    });
    
    setTimeout(() => {
      this.setupListeners();
      this.appInitializerService.initializeApp();
    }, 0);

    
    this.platform.ready().then(() => {
      this.checkAndHideSplashScreen();
      
      setTimeout(() => {
        this.setupVersionUpdates();
      }, 500);
      
      setTimeout(() => {
        this.validateAuthInBackground();
      }, 1500);
    });
    
  }
  
  async ngOnInit() {
    this.ionStorage = await this.storage.create();
    
    await this.checkOnboardingComplete();
    
    this.checkAndHideSplashScreen();
  }

  private checkAndHideSplashScreen() {
    setTimeout(() => {
      this.hideSplashScreen();
    }, 0);
  }
  
  /**
   * Hide splash screen and clean up timers
   */
  private hideSplashScreen() {
    requestAnimationFrame(() => {
      SplashScreen.hide();
    });
  }
  
  private setupVersionUpdates() {
    this.zone.runOutsideAngular(() => {
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt?.type) {
          case "VERSION_READY":
            this.promptUserToUpdate();
            break;
        }
      });
    });
  }

  /**
   * Validate auth state immediately for web or as background for native
   */
  private async validateAuthState() {
    if (this.authCheckDone) return;
    
    try {
      // Use the session directly from the linked signal
      const sessionValue = this.session();
      
      // Update storage and UI based on actual session
      if (sessionValue) {
        localStorage.setItem("isSignedIn", "true");
        this.isSignedIn.set(true);
      }
      
      this.authCheckDone = true;
    } catch (error) {
      console.error('Error validating auth:', error);
      this.isSignedIn.set(false);
    }
  }

  /**
   * Validate auth state in background without blocking UI for native platforms
   */
  private async validateAuthInBackground() {
    if (!Capacitor.isNativePlatform()) {
      // For web, we already did this in ngOnInit
      return;
    }
    
    // Do the validation
    await this.validateAuthState();
  }

  async checkOnboardingComplete() {
    if (!isPlatform("capacitor")) {
      this.onboardingComplete.set(true);
      return;
    }
    
    const value = await this.ionStorage.get("onboardingComplete");
    this.onboardingComplete.set(value ? true : false);
  }

  completeOnboarding() {
    this.ionStorage.set("onboardingComplete", true);
    this.onboardingComplete.set(true);
  }

  async setupListeners() {
    // Set up app URL open handler
    App.addListener("appUrlOpen", async (data: URLOpenListenerEvent) => {
      this.processAppUrl(data.url);
    });

    // Set up network status handler
    this.setupNetworkListener();
  }
  
  private async processCurrentUrl() {
    const url = window?.location?.href;
    if (!url) return;
    
    if (this.isGoogleCalendarCallback(url)) {
      this.processGoogleCalendarCallback(url);
    } else if (url?.includes("#error=access_denied")) {
      this.processErrorCallback(url);
    } else if (this.isStripeCallback(url)) {
      this.processStripeCallback(url);
    } else if (this.isVerifyEmailCallback(url)) {
      this.processVerifyEmailCallback(url);
    } else if (url?.includes("#access_token")) {
      this.processTokenCallback(url);
    }
  }
  
  private isGoogleCalendarCallback(url: string): boolean {
    return url?.includes("#access_token=") &&
      url?.includes("&refresh_token=") &&
      url?.includes("&provider_token=") &&
      url?.includes("&provider_refresh_token=") &&
      !url?.includes("apple-callback");
  }
  
  private isStripeCallback(url: string): boolean {
    return url?.includes("stripe_payment_id=")
  }

  private isVerifyEmailCallback(url: string): boolean {
    return url?.includes("verify_email?token_hash=")
  }

  private async processGoogleCalendarCallback(url: string) {
    const access = url.split("#access_token=")?.pop()?.split("&")[0];
    const refresh = url.split("&refresh_token=")?.pop()?.split("&")[0];
    const providerToken = url.split("&provider_token=")?.pop()?.split("&")[0];
    const providerRefreshToken = url
      .split("&provider_refresh_token=")
      ?.pop()
      ?.split("&")[0];

    if (access && refresh && providerToken && providerRefreshToken) {
      this.setupGoogleCalendar(
        access,
        refresh,
        providerToken,
        providerRefreshToken
      );
    }
  }

  private async processStripeCallback(url: string) {
    const stripePaymentId = url.split("stripe_payment_id=")?.pop()?.split("&")[0];
    if (stripePaymentId) {
      const url = `https://buy.stripe.com/${stripePaymentId}`;
      // redirect to the url
      window.location.href = url;
    }
  }
  
  private async processErrorCallback(url: string) {
    if (
      url?.includes("error_description=Email+link+is+invalid+or+has+expired")
    ) {
      const errorToast = await this.toastController.create({
        message:
          "Dieser Link ist ungültig oder abgelaufen.",
        position: "bottom",
        color: "danger",
        duration: 5000,
      });

      errorToast.present();
    } else {
      const errorToast = await this.toastController.create({
        message:
          "Ups, da ist leider ein Fehler aufgetreten. Bitte versuche es erneut.",
        position: "bottom",
        color: "danger",
        duration: 5000,
      });

      errorToast.present();
      this.authService.signOut();
    }
  }

  private async processVerifyEmailCallback(url: string) {
    const tokenHash = url.split("verify_email?token_hash=")?.pop()?.split("&")[0];
    if (tokenHash) {
      this.authService.verifyEmail(tokenHash).then(async () => {
        const successToast = await this.toastController.create({
          message: "Email bestätigt",
          position: "bottom",
          color: "success",
          duration: 3000,
        });
        successToast.present();
        this.router.navigateByUrl("/home/tab0", { replaceUrl: true });
      });
    }
  }
  
  private async processTokenCallback(url: string) {
    const access_token = url.split("#access_token=")?.pop()?.split("&")[0];
    const refresh_token = url.split("&refresh_token=")?.pop()?.split("&")[0];
    if (access_token && refresh_token) {
      await this.authService.setSession(access_token, refresh_token);
      const successToast = await this.toastController.create({
        message: "Account bestätigt",
        position: "bottom",
        color: "success",
        duration: 3000,
      });
      successToast.present();
      if (url?.includes("reset-password")) {
        this.router.navigateByUrl("/reset-password", { replaceUrl: true });
      } else {
        this.router.navigateByUrl("/home/tab0", { replaceUrl: true });
      }
    }
  }
  
  private async processAppUrl(openUrl: string) {
    if (this.isGoogleCalendarCallback(openUrl)) {
      this.processGoogleCalendarCallback(openUrl);
    } else if (this.isStripeCallback(openUrl)) {
      this.processStripeCallback(openUrl);
    } else if (openUrl?.includes("#error=access_denied")) {
      this.processErrorCallback(openUrl);
    } else if (this.isVerifyEmailCallback(openUrl)) {
      this.processVerifyEmailCallback(openUrl);
    } else if (openUrl?.includes("#access_token")) {
      this.processTokenCallback(openUrl);
    }
  }
  
  private async setupNetworkListener() {
    this.noNetworkToast = await this.toastController.create({
      message:
        "Du bist offline. Bitte verbinde dich mit dem Internet, um Quick Drive weiter zu verwenden.",
      position: "bottom",
      color: "danger",
    });

    Network.addListener("networkStatusChange", async (status) => {
      if (!status.connected) {
        this.notConnected = true;
        this.noNetworkToast.present();
      }
      if (status.connected && this.notConnected) {
        this.noNetworkToast.dismiss();
        this.notConnected = false;
        window.location.reload();
      }
    });
  }

  async setupGoogleCalendar(
    access,
    refresh,
    providerToken,
    providerRefreshToken
  ) {
    const loading = await this.loadingCtrl.create({
      message: "Kalendar synchronisieren...",
    });

    loading.present();

    try {
      await this.authService.setSession(access!, refresh!);
      const customer = await firstValueFrom(this.dataService.getCustomer());
  
      await this.calendarSyncService.setupGoogleCalendar(
        customer,
        providerToken,
        providerRefreshToken
      );
  
      this.presentSuccessAuth();
    } finally {
      loading.dismiss();
    }
  }

  async presentSuccessAuth() {
    const toast = await this.toastController.create({
      message: "Google Kalender synchronisiert",
      duration: 2000,
      color: "success",
    });
    toast.present();
  }

  async promptUserToUpdate() {
    const alert = await this.alertController.create({
      header: "Update verfügbar",
      message:
        "Eine neue Version von Quick Drive ist verfügbar. Möchtest du jetzt aktualisieren?",
      buttons: [
        {
          text: "Später",
          role: "cancel",
        },
        {
          text: "Aktualisieren",
          handler: () => {
            window.location.reload();
          },
        },
      ],
    });

    await alert.present();
  }

  private checkLocalStorageAuth() {
    try {
      const localStorageAuth = localStorage.getItem('isSignedIn');
      console.log('localStorageAuth', localStorageAuth);
      if (localStorageAuth === 'true') {
        this.isSignedIn.set(true);
      } else if (localStorageAuth === 'false') {
        this.isSignedIn.set(false);
      }
    } catch (e) {
      console.error('Error reading from localStorage:', e);
    }
  }
}
